import { Show, createSignal } from "solid-js";
import { Modal } from "~/ui/Modal";
import type { TeacherClass } from "./classes";

const CODE_CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
const CODE_RE = /^[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{8}$/;

function randomCode() {
  return Array.from({ length: 8 }, () => CODE_CHARS[Math.floor(Math.random() * CODE_CHARS.length)]).join("");
}

export function ChangeJoinCodeModal(props: {
  value: TeacherClass;
  onClose: () => void;
  onSave: (newCode: string) => Promise<void>;
}) {
  const [input, setInput] = createSignal(props.value.join_code);
  const [pending, setPending] = createSignal<string | null>(null);
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal("");

  const handleInput = (raw: string) => {
    setError("");
    setInput(raw.toUpperCase().replace(/[^ABCDEFGHJKLMNPQRSTUVWXYZ23456789]/g, "").slice(0, 8));
  };

  const submit = (event: SubmitEvent) => {
    event.preventDefault();
    const code = input().trim();
    if (!CODE_RE.test(code)) {
      return setError("Der Code muss genau 8 Zeichen haben (A–Z ohne I/O, Ziffern 2–9).");
    }
    if (code === props.value.join_code) {
      return setError("Das ist bereits der aktuelle Code.");
    }
    setPending(code);
  };

  const save = async (code: string) => {
    setBusy(true);
    setError("");
    try {
      await props.onSave(code);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Fehler beim Speichern.");
      setPending(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Klassencode ändern" onClose={props.onClose}>
      <Show when={pending()} fallback={
        <form onSubmit={submit}>
          <p style={{ "margin-bottom": "16px", "font-size": "0.9rem", color: "var(--text-muted)" }}>
            Schüler verwenden diesen Code um der Klasse beizutreten.
            Nach der Änderung müssen sie den neuen Code verwenden.
          </p>
          <div class="form-group">
            <label class="form-label" for="join-code-input">Neuer Klassencode</label>
            <div style={{ display: "flex", gap: "8px", "align-items": "center" }}>
              <input
                id="join-code-input"
                class="form-input"
                style={{ "font-family": "monospace", "font-size": "1.2rem", "letter-spacing": "3px", "text-transform": "uppercase", "max-width": "180px" }}
                value={input()}
                maxlength={8}
                autocomplete="off"
                autofocus
                onInput={(e) => handleInput(e.currentTarget.value)}
              />
              <button
                type="button"
                class="btn btn-secondary"
                title="Zufälligen Code würfeln"
                onClick={() => { setError(""); setInput(randomCode()); }}
              >
                🎲 Würfeln
              </button>
            </div>
            <p style={{ "font-size": "0.78rem", color: "var(--text-muted)", "margin-top": "6px" }}>
              8 Zeichen · Buchstaben A–Z (ohne I und O) und Ziffern 2–9
            </p>
          </div>
          {error() && <div class="form-error" style={{ "margin-bottom": "12px" }} role="alert">{error()}</div>}
          <button class="btn btn-primary btn-full" type="submit">Weiter</button>
        </form>
      }>
        {(code) => (
          <div>
            <p style={{ "margin-bottom": "16px" }}>Klassencode wirklich ändern?</p>
            <div style={{ background: "var(--surface-2, #f4f4f5)", "border-radius": "8px", padding: "12px 16px", "margin-bottom": "12px", "font-size": "0.9rem" }}>
              <div style={{ display: "flex", "align-items": "center", gap: "10px" }}>
                <span style={{ "font-family": "monospace", "letter-spacing": "2px", color: "var(--text-muted)", "text-decoration": "line-through" }}>{props.value.join_code}</span>
                <span style={{ color: "var(--text-muted)" }}>→</span>
                <strong style={{ "font-family": "monospace", "letter-spacing": "2px" }}>{code()}</strong>
              </div>
            </div>
            <p style={{ "font-size": "0.82rem", color: "var(--text-muted)", "margin-bottom": "16px" }}>
              Bestehende Schüler in der Klasse bleiben. Neue Schüler müssen den neuen Code verwenden.
            </p>
            {error() && <div class="form-error" style={{ "margin-bottom": "12px" }} role="alert">{error()}</div>}
            <div style={{ display: "flex", gap: "8px" }}>
              <button class="btn btn-primary" type="button" disabled={busy()} onClick={() => void save(code())}>
                {busy() ? "Wird gespeichert…" : "Ja, Code ändern"}
              </button>
              <button class="btn btn-secondary" type="button" disabled={busy()} onClick={() => setPending(null)}>
                Zurück
              </button>
            </div>
          </div>
        )}
      </Show>
    </Modal>
  );
}
